import React, { useState, useEffect } from 'react';
import { useBitacoraTimeline } from '../../../../utils/useBitacoraTimeline';
import { estadoGestionCroselling, getPersonaChipClass } from '../../../../utils/styles';
import { saveToFirestore } from '../../../../firebase/cleanPayload';

const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '-';
    if (typeof dateStr === 'string' && dateStr.includes('-')) {
        const parts = dateStr.split('T')[0].split('-');
        if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateStr;
};

// FORMATEADOR DIRECTO QUE LEE EXCLUSIVAMENTE FECHACREACION DE FIRESTORE O OBJETO DATE
const formatFechaObservacion = (obs) => {
    if (!obs) return '-';

    const fc = obs.fechaCreacion || obs.fecha || obs.createdAt;
    if (!fc) return new Date().toLocaleDateString('es-AR', { dateStyle: 'short' });

    if (typeof fc.toDate === 'function') {
        return fc.toDate().toLocaleDateString('es-AR', { dateStyle: 'short' });
    }
    if (fc.seconds) {
        return new Date(fc.seconds * 1000).toLocaleDateString('es-AR', { dateStyle: 'short' });
    }
    if (fc instanceof Date) {
        return fc.toLocaleDateString('es-AR', { dateStyle: 'short' });
    }
    if (typeof fc === 'string' && fc.trim() !== '') {
        return formatDateDisplay(fc);
    }

    return new Date().toLocaleDateString('es-AR', { dateStyle: 'short' });
};

const DetalleClienteCroselling = ({ record, onBack, onEdit }) => {
    const [newNote, setNewNote] = useState('');
    const [noteDate, setNoteDate] = useState(new Date().toISOString().split('T')[0]);
    
    // PRÓXIMO CONTACTO INICIA SIEMPRE LIMPIO
    const [proximaAgenda, setProximaAgenda] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const observacionesPath = record?.id 
        ? `public/data/clientes_croselling/${record.id}/observaciones` 
        : null;

    const {
        items: observaciones,
        loading,
        editingId,
        setEditingId,
        fetchNotes,
        removeLocalNote
    } = useBitacoraTimeline('comentario');

    useEffect(() => {
        if (observacionesPath) {
            fetchNotes(observacionesPath);
        }
    }, [observacionesPath, fetchNotes]);

    if (!record) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-[#f3f4f8] text-slate-400 font-bold uppercase tracking-widest text-xs animate-pulse">
                Cargando detalle del registro...
            </div>
        );
    }

    const telefonosList = Array.isArray(record?.telefonos) 
        ? record.telefonos 
        : (record?.telefono ? [record.telefono] : []);

    const emailsList = Array.isArray(record?.emails) 
        ? record.emails 
        : (record?.email ? [record.email] : []);

    // GUARDADO DIRECTO Y EFECTIVO EN FIRESTORE
    const handleSaveNote = async () => {
        if (!newNote.trim() || isSaving) return;

        setIsSaving(true);
        try {
            // Construimos la fecha exacta seleccionada
            const now = new Date();
            const [y, m, d] = noteDate.split('-').map(Number);
            const customDate = new Date(y, m - 1, d, now.getHours(), now.getMinutes(), now.getSeconds());

            // 1. Guardar la observación directamente en la subcolección de Firestore
            const notePayload = {
                tipoGestion: 'Observación',
                comentario: newNote.trim(),
                fechaCreacion: customDate,
                fechaProximoContacto: proximaAgenda || null
            };

            await saveToFirestore(observacionesPath, notePayload, editingId || null);

            // 2. Si se especificó una nueva fecha de próximo contacto, actualizar el documento raíz del cliente
            if (record?.id && proximaAgenda && proximaAgenda !== record.fechaProximoContacto) {
                await saveToFirestore('public/data/clientes_croselling', {
                    fechaProximoContacto: proximaAgenda
                }, record.id);
            }

            // 3. Refrescar el historial directamente desde Firestore
            if (observacionesPath) {
                await fetchNotes(observacionesPath);
            }

            // 4. Limpieza estricta de formulario
            setNewNote('');
            setNoteDate(new Date().toISOString().split('T')[0]);
            setProximaAgenda('');
            setEditingId(null);
        } catch (error) {
            console.error("Error al guardar nota y agenda en Firestore:", error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleEditNote = (obs) => {
        setEditingId(obs.id);
        
        const rawDate = obs.fechaCreacion || obs.fecha;
        let dateStr = new Date().toISOString().split('T')[0];
        
        if (typeof rawDate === 'string') {
            dateStr = rawDate.split('T')[0];
        } else if (rawDate?.seconds) {
            dateStr = new Date(rawDate.seconds * 1000).toISOString().split('T')[0];
        } else if (rawDate instanceof Date) {
            dateStr = rawDate.toISOString().split('T')[0];
        }

        setNewNote(obs.comentario || obs.comentarios || obs.observacion || '');
        setNoteDate(dateStr);
        setProximaAgenda(obs.fechaProximoContacto || obs.fechaProximaGestion || '');
    };

    const handleRemoveNote = async (idToDelete) => {
        removeLocalNote(idToDelete, observacionesPath);
    };

    return (
        <div className="min-h-screen bg-[#f3f4f8] text-left font-sans antialiased px-6 sm:px-10 pt-8 pb-24">
            <div className="max-w-7xl mx-auto space-y-6">
                
                {/* BARRA SUPERIOR DE NAVEGACIÓN */}
                <div className="flex items-center justify-between flex-wrap gap-4 bg-white/60 backdrop-blur-md p-3 px-5 rounded-2xl border border-slate-200/80 shadow-xs">
                    <button 
                        onClick={onBack} 
                        className="inline-flex items-center gap-2.5 text-xs font-bold text-slate-600 hover:text-slate-900 bg-white hover:bg-slate-50 border border-slate-200 px-4 py-2 rounded-xl transition-all shadow-xs active:scale-95 cursor-pointer uppercase tracking-wider"
                    >
                        <span className="text-sm">←</span> 
                        <span>Volver al Tablero</span>
                    </button>

                    {onEdit && (
                        <button
                            onClick={() => onEdit(record)}
                            className="bg-amber-50/80 hover:bg-amber-100/80 text-amber-800 border border-amber-300/80 font-bold text-xs px-4 py-2 rounded-xl transition-all shadow-2xs active:scale-95 flex items-center gap-2 uppercase tracking-wider cursor-pointer"
                            title="Editar datos del cliente, estado y agenda"
                        >
                            <svg className="w-3.5 h-3.5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                            <span>Editar Registro</span>
                        </button>
                    )}
                </div>

                {/* HEADER CLIENTE CON EDITAR ESTADO RÁPIDO */}
                <div className="bg-white border border-slate-200/90 rounded-3xl p-6 flex flex-col md:flex-row justify-between md:items-center gap-6 shadow-xs">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-blue-50 border border-blue-100 rounded-2xl text-blue-600 shrink-0">
                            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                        </div>
                        <div>
                            <div className="flex items-center gap-3 flex-wrap">
                                <h1 className="text-2xl font-bold text-slate-900 uppercase">
                                    {record.displayNombre}
                                </h1>

                                <div className="flex items-center gap-1.5">
                                    <span className={`px-3.5 py-1 rounded-full text-[11px] font-black uppercase tracking-wider border shadow-2xs ${estadoGestionCroselling(record.estadoGestion)}`}>
                                        ● {record.estadoGestion || 'Pendiente'}
                                    </span>

                                    {onEdit && (
                                        <button
                                            type="button"
                                            onClick={() => onEdit(record)}
                                            className="p-1.5 text-slate-400 hover:text-amber-600 bg-slate-50 hover:bg-amber-50 border border-slate-200 hover:border-amber-300 rounded-lg transition-all cursor-pointer shadow-2xs active:scale-90"
                                            title="Cambiar estado de gestión o editar ficha"
                                        >
                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center gap-3 text-xs font-mono text-slate-500 mt-1.5 flex-wrap">
                                <span>CUIT / DNI: <strong className="text-slate-800 font-bold">{record.displayDoc}</strong></span>
                                <span className="text-slate-300">•</span>
                                <span>Fecha Ingreso: <strong className="text-slate-800 font-bold">{formatDateDisplay(record.fechaCarga)}</strong></span>
                            </div>
                        </div>
                    </div>

                    {record.fechaProximoContacto ? (
                        <div className="bg-rose-50 border border-rose-200 rounded-2xl px-5 py-3 flex items-center gap-3 shrink-0 shadow-2xs">
                            <div className="p-2 bg-white text-rose-600 rounded-xl border border-rose-100 shadow-2xs">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <div>
                                <span className="block text-[10px] font-black text-rose-500 uppercase tracking-widest">Próximo Contacto</span>
                                <span className="text-sm font-mono font-black text-rose-700">
                                    {formatDateDisplay(record.fechaProximoContacto)}
                                </span>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-slate-50 border border-slate-200 rounded-2xl px-5 py-3 flex items-center gap-3 shrink-0">
                            <span className="text-xs font-bold text-slate-400">Sin agendar</span>
                        </div>
                    )}
                </div>

                {/* PANEL PRINCIPAL */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    
                    {/* COLUMNA IZQUIERDA: FICHA Y DATOS DE CONTACTO */}
                    <div className="lg:col-span-4 space-y-6">
                        <div className="bg-white border border-slate-200/90 rounded-3xl p-6 shadow-sm space-y-6">
                            
                            <div className="border-b border-slate-100 pb-3 flex items-center justify-between">
                                <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest">
                                    Ficha de Oportunidad
                                </h3>
                                <span className="text-[10px] bg-blue-50 text-blue-700 font-black px-2.5 py-0.5 rounded-full uppercase border border-blue-100">
                                    Cross-Selling
                                </span>
                            </div>

                            {/* RESPONSABLE ASIGNADO */}
                            <div className="space-y-2">
                                <span className="block text-xs font-extrabold text-slate-700 uppercase tracking-wider">
                                    Responsable Asignado
                                </span>
                                {record.responsableAsignado ? (
                                    <div className={`flex items-center gap-2 px-4 py-2 rounded-full w-fit tracking-wide ${getPersonaChipClass(record.responsableAsignado)}`}>
                                        <span className="text-[11px] font-bold uppercase">{record.responsableAsignado}</span>
                                    </div>
                                ) : (
                                    <span className="text-xs font-bold text-slate-400 italic block bg-slate-50 p-3 rounded-xl border border-slate-200/60">
                                        Sin responsable asignado
                                    </span>
                                )}
                            </div>

                            {/* DATOS DE CONTACTO */}
                            <div className="pt-3 border-t border-slate-100 space-y-3">
                                <div>
                                    <span className="block text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
                                        Teléfonos
                                    </span>
                                    {telefonosList.length > 0 ? (
                                        <div className="space-y-1">
                                            {telefonosList.map((tel, idx) => (
                                                <a key={idx} href={`tel:${tel}`} className="flex items-center gap-2 text-xs font-mono font-bold text-slate-800 hover:text-blue-600 transition-colors">
                                                    <span>📞</span> {tel}
                                                </a>
                                            ))}
                                        </div>
                                    ) : (
                                        <span className="text-xs font-bold text-slate-300">-</span>
                                    )}
                                </div>

                                <div>
                                    <span className="block text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
                                        Emails
                                    </span>
                                    {emailsList.length > 0 ? (
                                        <div className="space-y-1">
                                            {emailsList.map((mail, idx) => (
                                                <a key={idx} href={`mailto:${mail}`} className="flex items-center gap-2 text-xs font-mono font-bold text-slate-800 hover:text-blue-600 transition-colors truncate" title={mail}>
                                                    <span>✉️</span> <span className="truncate">{mail}</span>
                                                </a>
                                            ))}
                                        </div>
                                    ) : (
                                        <span className="text-xs font-bold text-slate-300">-</span>
                                    )}
                                </div>
                            </div>

                            {/* RAMOS Y PRODUCTOS */}
                            <div className="space-y-2 pt-3 border-t border-slate-100">
                                <span className="block text-xs font-extrabold text-slate-700 uppercase tracking-wider">
                                    Productos Sugeridos
                                </span>
                                <div className="flex flex-wrap gap-1.5">
                                    {Array.isArray(record.productoAprobar) && record.productoAprobar.length > 0 ? (
                                        record.productoAprobar.map(p => (
                                            <span key={p} className="bg-blue-50 text-blue-700 border border-blue-200 text-xs font-extrabold px-3 py-1.5 rounded-xl shadow-2xs">
                                                {p}
                                            </span>
                                        ))
                                    ) : (
                                        <span className="text-xs font-extrabold text-blue-700 bg-blue-50 border border-blue-200 px-3 py-1.5 rounded-xl">
                                            {record.productosDisplay || '-'}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* ESTRUCTURA COMERCIAL */}
                            <div className="pt-4 border-t border-slate-100 space-y-3">
                                <div className="bg-slate-50 p-3.5 rounded-2xl border border-slate-200/80">
                                    <span className="block text-[10px] font-black text-slate-400 uppercase tracking-wider">Canal / Org</span>
                                    <span className="text-xs font-bold text-slate-800 mt-0.5 block uppercase">{record.canal || 'DIRECTO'}</span>
                                </div>

                                <div className="bg-slate-50 p-3.5 rounded-2xl border border-slate-200/80">
                                    <span className="block text-[10px] font-black text-slate-400 uppercase tracking-wider">Nombre Canal</span>
                                    <span className="text-xs font-bold text-slate-800 mt-0.5 block uppercase">{record.canalNombre || '-'}</span>
                                </div>

                                <div className="bg-slate-50 p-3.5 rounded-2xl border border-slate-200/80">
                                    <span className="block text-[10px] font-black text-slate-400 uppercase tracking-wider">Subcanal / Asociado</span>
                                    <span className="text-xs font-bold text-slate-800 mt-0.5 block uppercase">{record.subcanalNombre || '-'}</span>
                                </div>
                            </div>

                        </div>
                    </div>

                    {/* COLUMNA DERECHA: TIMELINE Y REDACCIÓN CON PERSISTENCIA DIRECTA */}
                    <div className="lg:col-span-8 space-y-6">
                        <div className="bg-white border border-slate-200/90 rounded-3xl p-6 shadow-sm space-y-6">
                            
                            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                                <div>
                                    <h3 className="text-sm font-black text-slate-900 uppercase">
                                        Observaciones de Gestión
                                    </h3>
                                    <p className="text-xs text-slate-400 font-semibold mt-0.5">Seguimiento de llamadas, cotizaciones y compromisos</p>
                                </div>
                                <span className="text-xs font-mono font-extrabold bg-slate-100 text-slate-700 px-3 py-1 rounded-xl border border-slate-200">
                                    {observaciones.length} {observaciones.length === 1 ? 'registro' : 'registros'}
                                </span>
                            </div>

                            {/* CAJA DE REDACCIÓN */}
                            <div className={`border rounded-2xl p-5 space-y-4 transition-all duration-300 ${
                                editingId 
                                    ? 'bg-amber-50/40 border-amber-300 shadow-md shadow-amber-500/5' 
                                    : 'bg-slate-100/70 border-slate-200/90'
                            }`}>
                                {editingId && (
                                    <div className="flex items-center gap-2 text-amber-700 font-black text-[11px] uppercase tracking-wider bg-amber-100/80 px-3 py-1 rounded-lg w-fit border border-amber-200">
                                        <span>✏️ Modo Edición de Nota</span>
                                    </div>
                                )}

                                {/* FILA SUPERIOR: FECHA DE NOTA Y PRÓXIMO CONTACTO */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-[11px] font-black text-slate-500 uppercase tracking-wider mb-1 pl-1">
                                            Fecha de Nota
                                        </label>
                                        <input 
                                            type="date" 
                                            value={noteDate}
                                            onChange={(e) => setNoteDate(e.target.value)}
                                            className="w-full bg-white border border-slate-300 rounded-xl px-3.5 py-2 text-xs font-bold text-slate-800 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100 transition-all h-[42px]"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-[11px] font-black text-rose-600 uppercase tracking-wider mb-1 pl-1">
                                            📅 Próximo Contacto (Agenda)
                                        </label>
                                        <input 
                                            type="date" 
                                            value={proximaAgenda}
                                            onChange={(e) => setProximaAgenda(e.target.value)}
                                            placeholder="dd/mm/aaaa"
                                            className="w-full bg-white border border-rose-200 rounded-xl px-3.5 py-2 text-xs font-bold text-rose-700 outline-none focus:border-rose-400 focus:ring-1 focus:ring-rose-100 transition-all h-[42px]"
                                        />
                                    </div>
                                </div>

                                {/* FILA PRINCIPAL: TEXTAREA AMPLIO */}
                                <div>
                                    <label className="block text-[11px] font-black text-slate-500 uppercase tracking-wider mb-1 pl-1">
                                        {editingId ? 'Editando Comentario' : 'Nuevo Avance / Observación'}
                                    </label>
                                    <textarea 
                                        rows="3"
                                        value={newNote}
                                        onChange={(e) => setNewNote(e.target.value)}
                                        placeholder="Registrá en detalle el resultado de la llamada, propuesta enviada o compromisos asumidos..."
                                        className="w-full bg-white border border-slate-300 rounded-xl p-3.5 text-sm font-medium text-slate-800 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100 transition-all resize-y min-h-[90px]"
                                    />
                                </div>

                                {/* BOTONERA DE ACCIÓN QUE GUARDA DIRECTO EN FIRESTORE Y RESETEA */}
                                <div className="flex justify-end gap-2 pt-1">
                                    {editingId && (
                                        <button 
                                            type="button" 
                                            onClick={() => { setEditingId(null); setNewNote(''); setProximaAgenda(''); setNoteDate(new Date().toISOString().split('T')[0]); }}
                                            className="text-xs font-extrabold text-slate-500 hover:text-slate-700 px-4 py-2 rounded-xl cursor-pointer transition-colors uppercase tracking-wider"
                                        >
                                            Cancelar
                                        </button>
                                    )}
                                    <button 
                                        type="button" 
                                        onClick={handleSaveNote}
                                        disabled={isSaving}
                                        className={`text-white font-extrabold text-xs px-6 py-2.5 rounded-xl transition-all shadow-xs cursor-pointer active:scale-95 uppercase tracking-wider flex items-center gap-2 ${
                                            editingId 
                                                ? 'bg-amber-600 hover:bg-amber-700 shadow-amber-500/20' 
                                                : 'bg-blue-600 hover:bg-blue-700 shadow-blue-500/20'
                                        } ${isSaving ? 'opacity-50 cursor-wait' : ''}`}
                                    >
                                        <span>{isSaving ? 'Guardando...' : editingId ? 'Actualizar Nota' : 'Agregar Avance y Agendar'}</span>
                                        <span>→</span>
                                    </button>
                                </div>
                            </div>

                            {/* TIMELINE CRONOLÓGICO CON AMBAS FECHAS VISIBLES */}
                            <div className="pt-2 space-y-4">
                                {loading ? (
                                    <p className="text-center text-slate-400 italic text-xs py-8 animate-pulse font-bold uppercase tracking-widest">
                                        Sincronizando bitácora comercial...
                                    </p>
                                ) : observaciones.length === 0 ? (
                                    <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-2xl bg-slate-50/50">
                                        <p className="text-xs font-black text-slate-400 uppercase tracking-wider">Sin historial de observaciones</p>
                                        <p className="text-[11px] text-slate-400 mt-1 font-semibold">Escribí tu primer avance en el panel superior.</p>
                                    </div>
                                ) : (
                                    <div className="relative pl-6 border-l-2 border-slate-200 space-y-4 ml-2">
                                        {observaciones.map((obs, idx) => {
                                            const isBeingEdited = editingId === obs.id;
                                            const proxContactoHist = obs.fechaProximoContacto || obs.fechaProximaGestion;

                                            return (
                                                <div key={obs.id || idx} className="relative group">
                                                    <div className={`absolute -left-[31px] top-3.5 w-3.5 h-3.5 rounded-full border-2 border-white transition-all shadow-2xs ${
                                                        isBeingEdited ? 'bg-amber-500 scale-125 ring-4 ring-amber-100' : 'bg-blue-600 group-hover:scale-125'
                                                    }`}></div>

                                                    <div className={`border rounded-2xl p-4 transition-all space-y-2.5 shadow-2xs ${
                                                        isBeingEdited 
                                                            ? 'bg-amber-50/60 border-amber-300 ring-2 ring-amber-100' 
                                                            : 'bg-slate-50 hover:bg-slate-100/80 border-slate-200/90'
                                                    }`}>
                                                        <div className="flex justify-between items-center flex-wrap gap-2">
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                {/* CHIP DE FECHA CREADA AMPLIO Y CLARO */}
                                                                <span className={`text-xs font-mono font-black uppercase tracking-wider px-3 py-1 rounded-lg border shadow-2xs ${
                                                                    isBeingEdited 
                                                                        ? 'bg-amber-100 text-amber-800 border-amber-200' 
                                                                        : 'bg-blue-50 text-blue-700 border-blue-200/80'
                                                                }`}>
                                                                    #{observaciones.length - idx} • {formatFechaObservacion(obs)}
                                                                </span>

                                                                {/* CHIP DE PRÓXIMO CONTACTO HISTÓRICO DESTACADO EN ROJO */}
                                                                {proxContactoHist && (
                                                                    <span className="text-xs font-mono font-black text-rose-700 bg-rose-50 border border-rose-200 px-3 py-1 rounded-lg shadow-2xs">
                                                                        📅 Próx. Contacto: {formatDateDisplay(proxContactoHist)}
                                                                    </span>
                                                                )}
                                                            </div>

                                                            <div className="flex gap-3 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                <button 
                                                                    type="button" 
                                                                    onClick={() => handleEditNote(obs)}
                                                                    className="text-[11px] font-bold text-blue-500 hover:text-blue-700 uppercase transition-colors cursor-pointer"
                                                                >
                                                                    Editar
                                                                </button>
                                                                <button 
                                                                    type="button" 
                                                                    onClick={() => handleRemoveNote(obs.id)}
                                                                    className="text-[11px] font-bold text-rose-400 hover:text-rose-600 uppercase transition-colors cursor-pointer"
                                                                >
                                                                    Eliminar
                                                                </button>
                                                            </div>
                                                        </div>

                                                        <p className="text-xs sm:text-sm text-slate-800 leading-relaxed font-semibold whitespace-pre-wrap pt-0.5">
                                                            {obs.comentario || obs.comentarios || obs.observacion}
                                                        </p>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default DetalleClienteCroselling;